// apps/cli/src/lib/bootstrap-finalize.ts
//
// Everything that has to happen on the founder's own machine once the cloud
// installer reaches `done`. This used to be a command the founder typed
// (`m8t bootstrap finish`), which was never a UX decision — it existed because
// three jobs could not run while the installer was running, so they were handed
// to the human as homework.
//
// Two of those three have proper homes now:
//
//   • the founder's data-plane grant moved INTO the installer (entrypoint.sh
//     step 3b), where it runs early enough to survive a later phase failing;
//   • the rest — the sign-in redirect URI, the local state, and the company-
//     profile seed — run from here, invoked by `bootstrap status --watch` at
//     the exact moment it observes `done`.
//
// `status --watch` is the right caller because it is the only thing in the
// bootstrap path where all three facts are true at once: it runs locally as the
// human (so it holds directory rights the installer's managed identity never
// will), it knows the gateway FQDN (which did not exist at `launch` time), and
// it is already there when the install completes.

import * as fs from "node:fs/promises";
import * as os from "node:os";
import * as path from "node:path";
import { LocalCliError } from "./errors.js";
import { colors } from "./output.js";
import type { StatusDoc } from "./bootstrap-status.js";
import { ensureGatewayRedirectUri } from "./app-reg.js";
import { discoverGateway } from "./discovery.js";
import { writeConfig } from "./config.js";
import { reactiveSeedOnInstallComplete } from "./company-profile-seed.js";
import { renderInstallSummary } from "./install-summary.js";
import {
  companionsSupportHost,
  installCompanion,
  statusCompanion,
  type CompanionInstallState,
} from "./companion-install.js";
import {
  convergeCompanionFromChannel,
  defaultLocalCompanionInstallOptions,
} from "../commands/companion/install.js";
import { buildPrereqDeps } from "./prereq-deps.js";
import { runUsagePhase } from "./prereq-run.js";

/**
 * Decide what `~/.m8t/repo-root` should say.
 *
 * This needs a rule now that it did not need before. `bootstrap finish` was a
 * command a founder typed once, from their clone, with `--repo-root "$(pwd)"`.
 * Its replacement runs from `bootstrap status --watch`, which is routine and
 * re-runnable — so an operator watching a finished install from their home
 * directory would otherwise overwrite a working marker with a directory that
 * holds no m8t checkout, and silently break the plugin and every bicep resolve.
 *
 * Pure, so the rule is testable without a filesystem.
 */
export function resolveRepoRootMarker(args: {
  /** `--repo-root`, when the caller passed it. */
  explicit?: string;
  cwd: string;
  /** Whether `cwd` actually looks like an m8t checkout. */
  cwdIsCheckout: boolean;
  /** What the marker already says, or null when there is none. */
  existing: string | null;
}): string {
  // The founder named it. Nothing outranks that, including our own sniffing.
  if (args.explicit !== undefined) return args.explicit;
  // Running from a real checkout: refresh, which is the founder's own case and
  // correctly fixes a marker left stale by an earlier install.
  if (args.cwdIsCheckout) return args.cwd;
  // Not a checkout, but something already points somewhere. Keep it — a guess
  // must never displace a value that was, at some point, deliberate.
  if (args.existing !== null && args.existing !== "") return args.existing;
  // Nothing to lose: write the best guess we have, exactly as before.
  return args.cwd;
}

/** `brain create` reads `<root>/brain-template/`, so its presence is the honest test. */
async function looksLikeCheckout(dir: string): Promise<boolean> {
  try {
    return (await fs.stat(path.join(dir, "brain-template"))).isDirectory();
  } catch { return false; }
}

export interface FinalizeArgs {
  /** The terminal status doc — must be `done`; the caller checks that. */
  doc: StatusDoc;
  subscriptionId: string;
  resourceGroup: string;
  /** Where the founder's clone lives. Defaults to the process cwd. */
  repoRoot?: string;
  /** Human-readable progress. In `--output json` the caller routes this to stderr. */
  stdout: (s: string) => void;
  stderr: (s: string) => void;
}

/** Seams, so the command tests can drive this without touching Azure or GitHub. */
export interface FinalizeDeps {
  discoverGateway: typeof discoverGateway;
  writeConfig: typeof writeConfig;
  ensureGatewayRedirectUri: typeof ensureGatewayRedirectUri;
  runUsagePhase: typeof runUsagePhase;
  reactiveSeed: typeof reactiveSeedOnInstallComplete;
  companionsSupportHost: typeof companionsSupportHost;
  /** What `m8t companion status` would say — the test for "is this a from-zero install?". */
  companionStatus: () => Promise<CompanionInstallState>;
  /** `platformVersion` is the release being installed, so the companion comes from IT. */
  convergeCompanion: (platformVersion?: string) => ReturnType<typeof convergeCompanionFromChannel>;
  homedir: () => string;
}

const defaultDeps: FinalizeDeps = {
  discoverGateway,
  writeConfig,
  ensureGatewayRedirectUri,
  runUsagePhase,
  reactiveSeed: reactiveSeedOnInstallComplete,
  companionsSupportHost,
  companionStatus: () => statusCompanion(defaultLocalCompanionInstallOptions()),
  convergeCompanion: (platformVersion?: string) =>
    convergeCompanionFromChannel(installCompanion, {
      ...(platformVersion !== undefined ? { platformVersion } : {}),
    }),
  homedir: () => os.homedir(),
};

/**
 * Point the founder's machine at the platform that just came up, authorize
 * sign-in to it, and seed their advisors.
 *
 * Every step is independently best-effort — the platform is already live and
 * nothing here can un-live it, so one failing step must never suppress the next.
 * The failures are NOT equal in loudness, and that is deliberate: a missing
 * redirect URI means the founder cannot log in at all, so it shouts and prints
 * the by-hand remedy; a deferred profile seed is a dim aside.
 */
export async function finalizeInstall(args: FinalizeArgs, deps: FinalizeDeps = defaultDeps): Promise<void> {
  const markerDir = path.join(deps.homedir(), ".m8t");

  // repo-root marker (needed by the m8t plugin, the opt-in persona render, and
  // deploy bicep resolution). Written first: it needs no network, so a founder
  // who is offline still gets the one piece of local state every other local
  // tool reads. See resolveRepoRootMarker for why it is not simply cwd.
  const markerPath = path.join(markerDir, "repo-root");
  const cwd = process.cwd();
  const existing = await fs.readFile(markerPath, "utf8").then((s) => s.trim()).catch(() => null);
  const repoRoot = resolveRepoRootMarker({
    ...(args.repoRoot !== undefined ? { explicit: args.repoRoot } : {}),
    cwd,
    cwdIsCheckout: await looksLikeCheckout(cwd),
    existing,
  });
  await fs.mkdir(markerDir, { recursive: true });
  await fs.writeFile(markerPath, `${repoRoot}\n`, "utf8");

  let webappUrl: string | undefined;
  try {
    // Scoped to the resource group this install just used — the state file names
    // it, so there is no guessing between deployments in a shared subscription.
    const d = await deps.discoverGateway({ subscriptionId: args.subscriptionId, interactive: false, resourceGroup: args.resourceGroup });
    await deps.writeConfig({
      gatewayUrl: d.gatewayUrl, gatewayClientId: d.gatewayClientId, gatewayTenantId: d.gatewayTenantId,
      subscriptionId: d.subscriptionId, containerAppResourceId: d.containerAppResourceId,
      cachedAt: new Date().toISOString(),
    });
    webappUrl = d.gatewayUrl;
    args.stdout(
      `${colors.success("✅ The platform is live and your local tools are pointed at it.")}\n` +
      `  gateway:  ${d.gatewayUrl}\n` +
      `  foundry:  ${args.doc.result?.foundryEndpoint ?? "-"}\n\n`,
    );

    // Authorize sign-in to the webapp we just pointed at. This is the ONLY step
    // in the whole bootstrap path that CAN do it — the installer runs as a
    // managed identity with no directory role, and at `launch` time this FQDN did
    // not exist yet. See ensureGatewayRedirectUri. Skipping it leaves a gateway
    // that answers HTTP 200 and then refuses every sign-in with AADSTS50011.
    //
    // Its own try/catch, and a LOUD failure rather than a dim aside: this is not
    // cosmetic like the summary or the profile seed. If it fails, the founder
    // cannot log into their own platform, and the message has to say exactly that
    // and exactly how to fix it by hand — a founder without directory rights (a
    // guest, or a BYO app reg they don't own) needs an admin, and silence would
    // leave them staring at an opaque Entra error code.
    try {
      await deps.ensureGatewayRedirectUri(d.gatewayClientId, new URL(d.gatewayUrl).host);
    } catch (e) {
      const err = e as LocalCliError;
      args.stderr(
        `  ${colors.error("⚠ could not authorize sign-in for your webapp:")} ${err.message}\n` +
        `  ${colors.hint(`Until this is fixed, opening ${d.gatewayUrl} will fail with AADSTS50011 (redirect URI mismatch).`)}\n` +
        `  ${colors.hint("Ask a directory admin to run:")}\n` +
        `  ${colors.hint(`    m8t bootstrap status   # (as an admin), or by hand:`)}\n` +
        `  ${colors.hint(`    az ad app update --id ${d.gatewayClientId} --set spa.redirectUris="['${d.gatewayUrl}']"  # (merge, do not overwrite)`)}\n\n`,
      );
    }

    // A BACKSTOP, no longer the primary. The founder's Foundry + Key Vault roles
    // are granted by the installer at step 3b, early enough to survive a later
    // phase failing — which is the whole reason they moved there. This pass stays
    // because the installer image and this CLI ship on independent version
    // ladders: a founder pinning an older `--installer-tag` gets a container that
    // never learned to grant them, and a silent lockout is a far worse outcome
    // than two redundant role reads on the happy path (where both roles already
    // exist and this is a no-op).
    //
    // The redirect URI is handled immediately above, so its row is filtered out
    // here rather than reported twice.
    try {
      const verdict = await deps.runUsagePhase(
        buildPrereqDeps({ subscription: args.subscriptionId, resourceGroup: args.resourceGroup }),
        { fix: true, onProgress: (m) => { args.stdout(colors.dim(`  ${m}\n`)); } },
      );
      const rows = verdict.results.filter((r) => r.slug !== "signin-redirect-uri");
      const unresolved = rows.filter((r) => r.status === "fail");
      if (unresolved.length === 0) {
        args.stdout(`  ${colors.success("✓ your account can reach Foundry and read the platform Key Vault")}\n\n`);
      }
      for (const r of unresolved) {
        args.stderr(
          `  ${colors.error("⚠ could not grant you access:")} ${r.detail}\n` +
          (r.remedy ? `  ${colors.hint(`Fix it with: ${r.remedy}`)}\n` : "") + "\n",
        );
      }
    } catch (e) {
      args.stderr(
        `  ${colors.error("⚠ could not check or grant your platform access:")} ${(e as Error).message}\n` +
        `  ${colors.hint("Run 'm8t prereqs --fix' when you can — until then your AI team will not load and worker deploys will fail.")}\n\n`,
      );
    }
  } catch (e) {
    const err = e as LocalCliError;
    args.stdout(`${colors.success("✅ The platform is live.")}\n\n`);
    args.stderr(
      `  ${colors.hint(`⚠ couldn't auto-point your local tools at the gateway: ${err.message}`)}\n` +
      (err.hint ? `  ${colors.hint(err.hint)}\n` : "") + "\n",
    );
  }

  // End-of-install summary — best-effort read of the brain-App org (basic
  // platform → no github-app.json → no team block).
  let brainOrg: string | null = null;
  try {
    const credsRaw = await fs.readFile(path.join(markerDir, "github-app.json"), "utf8");
    const creds = JSON.parse(credsRaw) as { org?: string };
    brainOrg = typeof creds.org === "string" ? creds.org : null;
  } catch { /* no creds → basic platform */ }
  args.stdout(renderInstallSummary({ webappUrl, brainOrg }) + "\n");

  args.stdout(
    `${colors.field("Optional local tooling for your coding agent:")}\n` +
    `  • Talk to your deployed workers from agent sessions (e.g. /stacey) — install the m8t plugin: guides/install/m8t-plugin.md.\n` +
    `  • Azure-capable MCP servers (microsoft/azure-skills, Microsoft Learn) — see the optional steps in guides/install.md.\n` +
    `  • Deploy workers conversationally with the opt-in persona skills — see guides/workers.md.\n` +
    `  • Make sure your m8t CLI is current: npm i -g @m8t-stack/cli@latest\n\n` +
    `${colors.field("Then open a brand new chat/session")} — new skills + MCP servers load only on a fresh start.\n`,
  );

  // Reactive company-profile seed — independent of discovery (needs only the endpoint).
  try {
    await deps.reactiveSeed({
      endpoint: args.doc.result?.foundryEndpoint,
      subscriptionId: args.subscriptionId,
      stdout: args.stdout,
    });
  } catch (e) {
    args.stderr(
      `  ${colors.dim(`(company-profile seed deferred: ${e instanceof Error ? e.message : String(e)} — run 'm8t bootstrap seed-profile' later)`)}\n`,
    );
  }

  // The desktop companions arrive with the platform: a first run puts Stacey
  // and Ezra on the desktop rather than leaving them behind a command nobody
  // knows to type. They are versioned and released on their own, so this takes
  // the build the release BEING INSTALLED pins — not whatever the channel calls
  // latest, which is a different question and not the one an install is asking.
  //
  // It runs LAST, and it says so before it starts, because it is the only step
  // here that blocks on a large download (~120 MB, buffered whole). It used to
  // run first: one interrupted fetch then cost the founder the summary, the
  // tooling pointers and the profile seed as well, and left a terminal whose
  // last line was about Key Vault roles — indistinguishable from a hang.
  //
  // Best-effort, like the summary and the seed: the platform is live either
  // way. But no outcome is silent. Two of the four are nobody's fault (a host
  // with no build — there is no Linux one; a release cut before the companions
  // existed), and they used to pass without a word, which is precisely where a
  // real failure went unnoticed through an entire install.
  if (webappUrl === undefined) {
    // No gateway resolved, so there is nothing for the companions to point at.
    // That failure already shouted above; this step stays quiet rather than
    // adding a second complaint about the same missing thing.
  } else if (!deps.companionsSupportHost()) {
    args.stdout(
      `Desktop companions are not built for ${process.platform}-${process.arch} — skipped.\n` +
      `  ${colors.hint("They are macOS and Windows only.")}\n\n`,
    );
  } else {
    // WHICH release to take the companion from.
    //
    // The pin belongs to a from-zero install: it answers "what does the release
    // I am installing carry?". It does NOT answer that on a re-run — this
    // command is documented as re-runnable, the version comes from the
    // installer's status blob, and `m8t platform update` never rewrites it. A
    // deployment updated since install would be handed its OLD companion, which
    // converge would install over a newer one and then fail against the running
    // app: a downgrade reported as "needs repair". That is the same lying
    // install this step exists to remove, so a machine that already has a
    // companion keeps the pre-existing `latest` read and re-runs behave exactly
    // as they did before.
    //
    // The predicate is `statusCompanion` — the same answer `m8t companion
    // status` prints — so the two surfaces cannot disagree about what
    // "installed" means. If it cannot tell, it assumes installed: the cautious
    // branch is the one that changes nothing.
    //
    // (The installer also omits `platformVersion` outright when its image was
    // built without a release descriptor, so there may be no pin to read at
    // all. Either way the copy below names the read it actually made.)
    const alreadyHasCompanion = await deps
      .companionStatus()
      .then((s) => s.state !== "not-installed")
      .catch(() => true);
    const pinned = alreadyHasCompanion ? undefined : args.doc.result?.platformVersion;
    args.stdout(
      (pinned === undefined
        ? "Putting Stacey and Ezra on your desktop — fetching the latest released build\n"
        : `Putting Stacey and Ezra on your desktop — fetching the build ${pinned} pins\n`) +
      `  ${colors.hint("(about 120 MB; on a slow link this takes a few minutes)")}\n`,
    );
    try {
      const companion = await deps.convergeCompanion(pinned);
      if (companion.state === "installed") {
        args.stdout("Desktop companions installed: Stacey and Ezra are ready.\n\n");
      } else if (companion.state === "not-released") {
        // Naming the release that was read is what turns a recurrence into a
        // self-reporting event: this line against a release that demonstrably
        // carries a companion says "we read the wrong manifest", which is
        // exactly the diagnosis the first occurrence could not produce.
        args.stdout(
          `${pinned === undefined
            ? "The latest platform release carries no desktop companions."
            : `The release this platform is pinned to (${pinned}) carries no desktop companions.`}\n` +
          "They arrive with a platform release — run: m8t platform update\n\n",
        );
      } else {
        // `converge` can also RETURN a bad state rather than throwing (an
        // install manifest it cannot vouch for, say). That is the same outcome
        // for the founder as a thrown failure, so it takes the same line — and
        // carries the state's own reason, which is the part worth reading.
        throw new Error(
          companion.state === "needs-repair"
            ? companion.reason
            : `companion installation is not verified (${companion.state})`,
        );
      }
    } catch (e) {
      const reason = e instanceof Error ? e.message : String(e);
      args.stdout(
        "Platform installation succeeded, but the desktop companions need repair.\n" +
        "Run: m8t companion repair\n" +
        `  ${colors.hint(reason)}\n\n`,
      );
    }
  }
}
